import useIsMobile from '@/hooks/use-is-mobile';
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { isCustomizeDressAttribute } from '../../isCustomizeDressAttribute';
import { forMatLabel } from '../../lib/format-label';
import type { ConfigurableOption, Item, ProductRoot, ShowAttribute } from '../../types/product';
import { SizeOptions } from '../size-options';
import { CustomizeDressColorOptions } from './customize-dress-color-options';
import { CustomizeDressFabricOptions } from './customize-dress-fabric-option';
import { CustomizeDressOptions } from './customize-dress-options';
import { CustomizeDressSizeOptions } from './customize-dress-size-options';

export const CustomizeDressStandardOptions = forwardRef(
  (
    {
      product,
      hasOnlyOneColor,
      outOfStockVariants,
      showSizeChart,
      showShopMoreInThisColor,
      maybeTabs,
      categoryUrlKey,
      createFabricButton,
      show_attribute,
      optionSpecs,
      handleClick,
      hasAvailableProduct,
      setSwatchAddToCartDialogVisible,
      showSwatchAddToCartTrigger = false,
      isAvailableProduct = false,
      crossPrice,
      customAttributeTreeData,
      activeOptionLabel,
      setActiveOptionLabel,
      chooseSizeVisible,
      setChooseSizeVisible,
    }: {
      product: ProductRoot;
      hasOnlyOneColor: boolean;
      outOfStockVariants: any;
      showSizeChart?: boolean;
      showShopMoreInThisColor?: boolean;
      maybeTabs?: React.ReactNode;
      categoryUrlKey?: string;
      createFabricButton?: React.ReactNode;
      show_attribute?: ShowAttribute[];
      handleClick: any;
      optionSpecs: any;
      hasAvailableProduct: boolean;
      setSwatchAddToCartDialogVisible?: (nextVisible: boolean) => void;
      showSwatchAddToCartTrigger?: boolean;
      isAvailableProduct?: boolean;
      crossPrice?: null | number;
      customAttributeTreeData: any;
      activeOptionLabel: any;
      setActiveOptionLabel: any;
      chooseSizeVisible: any;
      setChooseSizeVisible: any;
    },
    ref,
  ) => {
    const isMobile = useIsMobile();

    const mobileOptionsContainerRef = useRef<HTMLDivElement>(null);

    const options: Item[] | ConfigurableOption[] = useMemo(() => {
      if (product.__typename === 'SimpleProduct') {
        return product.ampProtOption?.items || [];
      }

      return product.configurable_options?.sort((a, b) => a.position - b.position);
    }, [product]);

    const isWeddingShoes = product?.categories?.[0]?.uid === 'MTYy';

    const { watch, resetField, getValues } = useFormContext();

    const formValues = watch();

    const variantStockStatusMap = useMemo(() => {
      if (!Array.isArray(product.variants) || product.variants.length === 0) {
        return {};
      }

      return product.variants.reduce<Record<string, 0 | 1>>((acc, cur) => {
        const isInStock = cur.product.stock_status === 'IN_STOCK';
        const { attributes } = cur;

        if (!Array.isArray(attributes) || attributes.length === 0) {
          return acc;
        }

        const valueIndexs = attributes.map(({ value_index }) => String(value_index));

        for (let i = 0, len = attributes.length; i < len; i++) {
          for (const valueIndexList of combine(valueIndexs, i + 1)) {
            const valueIndexGroup = valueIndexList.sort().join(',');

            if (isInStock) {
              acc[valueIndexGroup] = 1;
              continue;
            }

            if (!Object.prototype.hasOwnProperty.call(acc, valueIndexGroup)) {
              acc[valueIndexGroup] = 0;
            }
          }
        }

        return acc;
      }, {});
    }, [product.variants]);

    const getIsOutOfStock = (formFiled: string, valueIndex: string, nextFormValues = formValues) => {
      if (formFiled === 'Color' && isAvailableProduct) {
        return false;
      }

      if (product.__typename === 'SimpleProduct') {
        if (Array.isArray(product.ampProtOption?.items)) {
          return false;
        }

        return product.stock_status !== 'IN_STOCK';
      }

      if (product.__typename === 'ConfigurableProduct') {
        if (!options) {
          return false;
        }

        const valueIndexGroup = options
          .reduce(
            (acc, cur) => {
              let optionFormFiled = '';

              if (cur.label === 'Fabric') {
                optionFormFiled = 'Fabric';
              } else if (cur?.label?.toLowerCase()?.includes('color')) {
                optionFormFiled = 'Color';
              } else {
                optionFormFiled = forMatLabel(cur.label);
              }

              const selectedValueIndex = nextFormValues?.[optionFormFiled]?.value_index;

              if (optionFormFiled === formFiled || !selectedValueIndex) {
                return acc;
              }

              acc.push(String(selectedValueIndex));

              return acc;
            },
            [String(valueIndex)],
          )
          .sort()
          .join(',');

        return !variantStockStatusMap[valueIndexGroup];
      }

      return false;
    };

    const handleResetAvailableProductOtherAttr = (nextColorValue: any) => {
      if (!isAvailableProduct || !Array.isArray(options)) {
        return;
      }

      const otherKeys = options.reduce<string[]>((acc, cur) => {
        if (!cur || cur.label === 'Fabric' || cur?.label?.toLowerCase()?.includes('color')) {
          return acc;
        }

        acc.push(forMatLabel(cur.label));

        return acc;
      }, []);

      for (const field of otherKeys) {
        const prevValueIndex = getValues()[field]?.value_index;

        if (!prevValueIndex) {
          continue;
        }

        const isOutOfStock = getIsOutOfStock(field, prevValueIndex, {
          ...formValues,
          Color: nextColorValue,
        });

        if (isOutOfStock) {
          resetField(field);
        }
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        setActiveOptionLabel,
        mobileOptionsContainerRef,
      }),
      [setActiveOptionLabel],
    );

    const onOptionSelect = (label, event) => {
      setActiveOptionLabel(label);

      const container = mobileOptionsContainerRef.current;

      if (!container) return;

      const containerWidth = container.offsetWidth;

      const item = event.target;

      const itemWidth = item.offsetWidth;

      // 计算子元素的中心位置
      const itemOffsetLeft = item.offsetLeft + itemWidth / 2;

      // 计算滚动位置，使子元素居中
      const scrollPosition = itemOffsetLeft - containerWidth / 2;

      // 滚动到指定位置
      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth', // 平滑滚动
      });
    };

    const mobileOptionContent = options?.map((item, index) => {
      // fabric
      if (item?.label === 'Fabric') {
        return (
          <div
            key={item.uid}
            className={activeOptionLabel === 'Fabric' ? '' : 'hidden'}
          >
            <CustomizeDressFabricOptions
              item={item}
              createFabricButton={createFabricButton}
              handleClick={handleClick}
              clickIndex={index}
              optionSpecs={optionSpecs}
              product={product}
            />
          </div>
        );
      }

      // color
      if (item?.label?.toLowerCase()?.includes('color')) {
        return (
          <div
            key={item.uid}
            className={activeOptionLabel === 'Color' ? '' : 'hidden'}
          >
            <CustomizeDressColorOptions
              item={item as any}
              hasOnlyOneColor={hasOnlyOneColor}
              categoryUrlKey={categoryUrlKey || ''}
              options={options}
              outOfStockVariants={outOfStockVariants}
              product={product}
              showShopMoreInThisColor={showShopMoreInThisColor}
              maybeTabs={maybeTabs}
              handleClick={handleClick}
              clickIndex={index}
              optionSpecs={optionSpecs}
              hasAvailableProduct={hasAvailableProduct}
              setSwatchAddToCartDialogVisible={setSwatchAddToCartDialogVisible}
              showSwatchAddToCartTrigger={showSwatchAddToCartTrigger}
              getIsOutOfStock={getIsOutOfStock}
              onResetAvailableProductOtherAttr={handleResetAvailableProductOtherAttr}
              crossPrice={crossPrice}
            />
          </div>
        );
      }

      // Waist, Neckline, Strap Sleeves, Hem, Back

      if (isCustomizeDressAttribute(item.label)) {
        return (
          <div
            key={item.uid}
            className={activeOptionLabel === item.label ? '' : 'hidden'}
          >
            <CustomizeDressOptions
              item={item}
              product={product}
              key={item?.uid}
              customAttributeTreeData={customAttributeTreeData}
            />
          </div>
        );
      }

      // size or other
      return (
        <div
          key={item.uid}
          className={activeOptionLabel === item.label ? '' : 'hidden'}
        >
          <CustomizeDressSizeOptions
            item={item}
            show_attribute={show_attribute}
            isWeddingShoes={isWeddingShoes}
            outOfStockVariants={outOfStockVariants}
            product={product}
            showSizeChart={showSizeChart}
            variants={product?.variants}
            handleClick={handleClick}
            optionSpecs={optionSpecs}
            hasAvailableProduct={hasAvailableProduct}
            getIsOutOfStock={getIsOutOfStock}
            shippingType={(product?.shipping_type ?? 'Dresses') as 'Dresses' | 'Wedding Dresses'}
            chooseSizeVisible={chooseSizeVisible}
            setChooseSizeVisible={setChooseSizeVisible}
          />
        </div>
      );
    });

    const mobileOptions = (
      <div className='flex flex-col'>
        <div
          className='aw-thin-scroll-bar w-full overflow-x-auto px-[50%]'
          ref={mobileOptionsContainerRef}
        >
          <div className='flex min-w-max items-center gap-x-[30px] pb-[10px] pt-[20px]'>
            <div
              className={`whitespace-nowrap text-[17px] font-[500] uppercase leading-[1.4] ${activeOptionLabel === 'Fabric' ? 'text-[#040303]' : 'text-[#ABA6A0]'}`}
              onClick={event => {
                onOptionSelect('Fabric', event);
              }}
            >
              Fabric
            </div>
            <div
              className={`whitespace-nowrap text-[17px] font-[500] uppercase leading-[1.4] ${activeOptionLabel === 'Color' ? 'text-[#040303]' : 'text-[#ABA6A0]'}`}
              onClick={event => {
                onOptionSelect('Color', event);
              }}
            >
              Color
            </div>
            {options?.map((item, index) => {
              if (item.label !== 'Fabric' && !item?.label?.toLowerCase()?.includes('color')) {
                return (
                  <div
                    className={`whitespace-nowrap text-[17px] font-[500] uppercase leading-[1.4] ${activeOptionLabel === item.label ? 'text-[#040303]' : 'text-[#ABA6A0]'}`}
                    key={index}
                    onClick={event => {
                      onOptionSelect(item.label, event);
                    }}
                  >
                    {item.label}
                  </div>
                );
              }
            })}
          </div>
        </div>
        {mobileOptionContent}
      </div>
    );

    const desktopOptions = options?.map((item, index) => {
      // fabric
      if (item?.label === 'Fabric') {
        return (
          <CustomizeDressFabricOptions
            key={item.uid}
            item={item}
            createFabricButton={createFabricButton}
            handleClick={handleClick}
            clickIndex={index}
            optionSpecs={optionSpecs}
            product={product}
          />
        );
      }

      // color
      if (item?.label?.toLowerCase()?.includes('color')) {
        return (
          <CustomizeDressColorOptions
            item={item as any}
            hasOnlyOneColor={hasOnlyOneColor}
            key={item?.uid}
            categoryUrlKey={categoryUrlKey || ''}
            options={options}
            outOfStockVariants={outOfStockVariants}
            product={product}
            showShopMoreInThisColor={showShopMoreInThisColor}
            maybeTabs={maybeTabs}
            handleClick={handleClick}
            clickIndex={index}
            optionSpecs={optionSpecs}
            hasAvailableProduct={hasAvailableProduct}
            setSwatchAddToCartDialogVisible={setSwatchAddToCartDialogVisible}
            showSwatchAddToCartTrigger={showSwatchAddToCartTrigger}
            getIsOutOfStock={getIsOutOfStock}
            onResetAvailableProductOtherAttr={handleResetAvailableProductOtherAttr}
            crossPrice={crossPrice}
          />
        );
      }

      // Waist, Neckline, Strap Sleeves, Hem, Back

      if (isCustomizeDressAttribute(item.label)) {
        return (
          <CustomizeDressOptions
            item={item}
            product={product}
            key={item?.uid}
            customAttributeTreeData={customAttributeTreeData}
          />
        );
      }

      // size or other
      return (
        <SizeOptions
          item={item}
          key={item?.uid}
          show_attribute={show_attribute}
          isWeddingShoes={isWeddingShoes}
          outOfStockVariants={outOfStockVariants}
          product={product}
          showSizeChart={showSizeChart}
          variants={product?.variants}
          handleClick={handleClick}
          optionSpecs={optionSpecs}
          hasAvailableProduct={hasAvailableProduct}
          getIsOutOfStock={getIsOutOfStock}
          shippingType={(product?.shipping_type ?? 'Dresses') as 'Dresses' | 'Wedding Dresses'}
        />
      );
    });

    return isMobile ? mobileOptions : desktopOptions;
  },
);

const combine = (elements: (string | number)[], count: number) => {
  if (count > elements.length || count <= 0) {
    return [];
  }

  if (count === elements.length) {
    return [elements];
  }

  if (count === 1) {
    return elements.map(element => [element]);
  }

  const result = [];
  const stack: { start: number; comb: (string | number)[]; depth: number }[] = [
    {
      start: 0,
      comb: [],
      depth: 0,
    },
  ];
  const elementCount = elements.length;

  while (stack.length > 0) {
    const { start, comb, depth } = stack.pop()!;

    if (depth === count) {
      result.push(comb);
      continue;
    }

    for (let i = start; i <= elementCount - (count - depth); i++) {
      stack.push({
        start: i + 1,
        comb: comb.concat(elements[i]),
        depth: depth + 1,
      });
    }
  }

  return result;
};
